import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Download, Paperclip } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import MarkdownBody from "@/components/MarkdownBody";
import { toastError } from "@/lib/toast";
import { cn } from "@/lib/utils";
import {
  convertCsvToSheets,
  convertDocToHtml,
  convertDocxToHtml,
  convertXlsxToSheets,
  isCsvMime,
  isDocMime,
  isDocxMime,
  isOfficePreviewMime,
  isSpreadsheetMime,
} from "@/lib/officePreview";
import {
  isPostAttachmentPreviewable,
  normalizeAttachmentMime,
  postAttachmentContentUrl,
} from "@/lib/postAttachments";

/**
 * File chips for post attachments. Previewable types open a dialog; others download.
 */
export default function PostAttachmentChips({
  attachments = [],
  postId,
  orgId,
  accessToken,
  className,
  stopPropagation = true,
}) {
  const list = Array.isArray(attachments) ? attachments : [];
  const [preview, setPreview] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewText, setPreviewText] = useState(null);
  const [previewHtml, setPreviewHtml] = useState(null);
  const [previewSheets, setPreviewSheets] = useState(null);
  const [previewSheetIndex, setPreviewSheetIndex] = useState(0);
  const [loading, setLoading] = useState(false);
  const previewUrlRef = useRef(null);

  const clearPreviewMedia = () => {
    setPreviewText(null);
    setPreviewHtml(null);
    setPreviewSheets(null);
    setPreviewSheetIndex(0);
    if (previewUrlRef.current) {
      URL.revokeObjectURL(previewUrlRef.current);
      previewUrlRef.current = null;
    }
    setPreviewUrl(null);
  };

  useEffect(() => {
    return () => {
      if (previewUrlRef.current) {
        URL.revokeObjectURL(previewUrlRef.current);
      }
    };
  }, []);

  if (!list.length || !postId || !orgId || !accessToken) return null;

  const authHeaders = { Authorization: `Bearer ${accessToken}` };

  const downloadAttachment = async (attachment) => {
    const aid = attachment.attachment_id ?? attachment.attachmentId;
    const name = attachment.file_name || attachment.fileName || "file";
    const res = await axios.get(
      postAttachmentContentUrl(postId, aid, orgId, "attachment"),
      { headers: authHeaders, responseType: "blob" }
    );
    const url = URL.createObjectURL(res.data);
    const link = document.createElement("a");
    link.href = url;
    link.download = name;
    link.click();
    URL.revokeObjectURL(url);
  };

  const openPreview = async (attachment) => {
    if (!isPostAttachmentPreviewable(attachment)) {
      try {
        await downloadAttachment(attachment);
      } catch (err) {
        toastError(err, "Download failed");
      }
      return;
    }

    setPreview(attachment);
    clearPreviewMedia();
    setLoading(true);
    const aid = attachment.attachment_id ?? attachment.attachmentId;
    const mime = normalizeAttachmentMime(attachment);
    const name = attachment.file_name || attachment.fileName || "file";

    try {
      const res = await axios.get(
        postAttachmentContentUrl(postId, aid, orgId, "inline"),
        { headers: authHeaders, responseType: "blob" }
      );

      const isPlainText =
        (mime.startsWith("text/") ||
          mime === "text/csv" ||
          mime === "application/csv") &&
        !isCsvMime(mime);

      if (isOfficePreviewMime(mime)) {
        try {
          const arrayBuffer = await res.data.arrayBuffer();
          if (isDocMime(mime)) {
            setPreviewHtml(await convertDocToHtml(arrayBuffer));
          } else if (isDocxMime(mime)) {
            setPreviewHtml(await convertDocxToHtml(arrayBuffer));
          } else if (isSpreadsheetMime(mime)) {
            const sheets = await convertXlsxToSheets(arrayBuffer);
            setPreviewSheets(sheets);
            setPreviewSheetIndex(0);
          }
        } catch (convertErr) {
          toastError(convertErr, "Could not preview file — downloading instead");
          setPreview(null);
          clearPreviewMedia();
          await downloadAttachment(attachment);
        }
      } else if (isCsvMime(mime) || name.toLowerCase().endsWith(".csv")) {
        try {
          const text = await res.data.text();
          try {
            const sheets = convertCsvToSheets(text);
            setPreviewSheets(sheets);
            setPreviewSheetIndex(0);
          } catch {
            setPreviewText(text);
          }
        } catch (err) {
          toastError(err, "Could not preview CSV — downloading instead");
          setPreview(null);
          clearPreviewMedia();
          await downloadAttachment(attachment);
        }
      } else if (isPlainText) {
        setPreviewText(await res.data.text());
      } else {
        const url = URL.createObjectURL(res.data);
        previewUrlRef.current = url;
        setPreviewUrl(url);
      }
    } catch (err) {
      toastError(err, "Preview failed");
      setPreview(null);
      clearPreviewMedia();
    } finally {
      setLoading(false);
    }
  };

  const previewMime = normalizeAttachmentMime(preview);
  const previewName = preview?.file_name || preview?.fileName || "Preview";
  const previewIsMarkdown =
    previewMime === "text/markdown" ||
    String(previewName).toLowerCase().endsWith(".md");

  return (
    <>
      <div className={cn("flex flex-wrap gap-1.5", className)}>
        {list.map((a) => {
          const aid = a.attachment_id ?? a.attachmentId;
          const name = a.file_name || a.fileName || "file";
          const canPreview = isPostAttachmentPreviewable(a);
          return (
            <button
              key={aid}
              type="button"
              title={canPreview ? "Preview" : "Download"}
              onClick={(e) => {
                if (stopPropagation) e.stopPropagation();
                openPreview(a);
              }}
              className="inline-flex max-w-[14rem] items-center gap-1 rounded border border-border px-1.5 py-0.5 text-xs text-muted-foreground hover:text-foreground hover:bg-muted/50"
            >
              <Paperclip className="h-3 w-3 shrink-0" />
              <span className="truncate">{name}</span>
            </button>
          );
        })}
      </div>

      <Dialog
        open={!!preview}
        onOpenChange={(o) => {
          if (!o) {
            setPreview(null);
            clearPreviewMedia();
            setLoading(false);
          }
        }}
      >
        <DialogContent
          className="max-w-3xl max-h-[85vh] overflow-hidden flex flex-col"
          onClick={(e) => e.stopPropagation()}
        >
          <DialogHeader>
            <DialogTitle className="truncate pr-8">{previewName}</DialogTitle>
          </DialogHeader>
          {previewSheets?.length > 1 && (
            <div className="flex flex-wrap gap-1 shrink-0">
              {previewSheets.map((sheet, idx) => (
                <Button
                  key={`${sheet.name}-${idx}`}
                  type="button"
                  size="sm"
                  variant={idx === previewSheetIndex ? "default" : "outline"}
                  onClick={() => setPreviewSheetIndex(idx)}
                >
                  {sheet.name}
                </Button>
              ))}
            </div>
          )}
          <div className="flex-1 min-h-0 overflow-auto rounded-md border border-border bg-muted/20 p-2">
            {loading ? (
              <p className="text-sm text-muted-foreground p-4">Loading…</p>
            ) : previewText != null ? (
              previewIsMarkdown ? (
                <div className="p-3">
                  <MarkdownBody content={previewText} />
                </div>
              ) : (
                <pre className="text-xs whitespace-pre-wrap font-mono p-2">
                  {previewText}
                </pre>
              )
            ) : previewHtml != null ? (
              <div
                className="office-preview prose prose-sm dark:prose-invert max-w-none p-3 [&_table]:w-full [&_table]:border-collapse [&_td]:border [&_td]:border-border [&_td]:px-2 [&_td]:py-1 [&_th]:border [&_th]:border-border [&_th]:px-2 [&_th]:py-1 [&_th]:bg-muted/40"
                dangerouslySetInnerHTML={{ __html: previewHtml }}
              />
            ) : previewSheets != null ? (
              previewSheets.length === 0 ? (
                <p className="text-sm text-muted-foreground p-4">
                  This workbook has no sheets.
                </p>
              ) : (
                <div
                  className="office-preview prose prose-sm dark:prose-invert max-w-none p-3 overflow-x-auto [&_table]:w-full [&_table]:border-collapse [&_td]:border [&_td]:border-border [&_td]:px-2 [&_td]:py-1 [&_th]:border [&_th]:border-border [&_th]:px-2 [&_th]:py-1 [&_th]:bg-muted/40"
                  dangerouslySetInnerHTML={{
                    __html:
                      previewSheets[previewSheetIndex]?.html ||
                      previewSheets[0]?.html ||
                      "",
                  }}
                />
              )
            ) : previewUrl && previewMime.startsWith("image/") ? (
              <img
                src={previewUrl}
                alt={previewName}
                className="max-w-full max-h-[60vh] mx-auto object-contain"
              />
            ) : previewUrl && previewMime === "application/pdf" ? (
              <iframe
                title={previewName}
                src={previewUrl}
                className="w-full h-[60vh] rounded"
              />
            ) : (
              <p className="text-sm text-muted-foreground p-4">Loading…</p>
            )}
          </div>
          {preview && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="self-start"
              onClick={async () => {
                try {
                  await downloadAttachment(preview);
                } catch (err) {
                  toastError(err, "Download failed");
                }
              }}
            >
              <Download className="h-3.5 w-3.5 mr-1" />
              Download
            </Button>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
